import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, ExternalLink, GitPullRequest, Loader2, RotateCcw, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useEpicPullRequests, useMarkPullRequestReady, type EpicPullRequest } from "@/hooks/queries/use-pull-requests";
import { useContinueRun } from "@/hooks/queries/use-mailbox";
import { useQuery } from "@tanstack/react-query";
import { featuresApi } from "@/lib/api/features";
import { useToast } from "@/hooks/useToast";
import { ApiError } from "@/lib/api/client";
import type { Job } from "@/lib/api/mailbox";
import { cn } from "@/lib/utils";

const TERMINAL = new Set(["done", "failed", "cancelled"]);

interface PayloadFacts {
  epicId: string | null;
  orchestrate: boolean;
  fix: boolean;
  deferredIds: string[];
}

function payloadFacts(payload: unknown): PayloadFacts {
  const p = typeof payload === "object" && payload !== null ? (payload as Record<string, unknown>) : {};
  const deferred = Array.isArray(p.deferredFeatures) ? p.deferredFeatures : [];
  return {
    epicId: typeof p.epicId === "string" ? p.epicId : null,
    orchestrate: p.kind === "orchestrate",
    fix: p.fix !== undefined && p.fix !== null,
    deferredIds: deferred
      .map((f) => (typeof f === "object" && f !== null ? (f as Record<string, unknown>).id : null))
      .filter((id): id is string => typeof id === "string"),
  };
}

/** ER-130: whether the owner can press "All merged — continue" / "Restart" on this build. */
export function continueChoices(job: Pick<Job, "status" | "waitingReason" | "payload">): {
  canContinue: boolean;
  canRestart: boolean;
} {
  const facts = payloadFacts(job.payload);
  if (!facts.orchestrate || facts.fix || !facts.epicId) return { canContinue: false, canRestart: false };
  const parked = job.status === "waiting" && job.waitingReason === "needs_input";
  const stopped = TERMINAL.has(job.status);
  return { canContinue: parked || stopped, canRestart: stopped };
}

function ChecksBadge({ pr }: { pr: EpicPullRequest }) {
  if (pr.state !== "open" || !pr.checks || pr.checks.total === 0) return null;
  const { failed, pending, passed, total } = pr.checks;
  if (failed > 0)
    return (
      <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
        <XCircle className="h-3 w-3 mr-1" />
        {failed} check{failed === 1 ? "" : "s"} failed
      </Badge>
    );
  if (pending > 0)
    return (
      <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
        Checks running ({passed}/{total})
      </Badge>
    );
  return (
    <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
      <CheckCircle2 className="h-3 w-3 mr-1" />
      CI green
    </Badge>
  );
}

function ciGreen(pr: EpicPullRequest): boolean {
  return pr.checks !== null && pr.checks.failed === 0 && pr.checks.pending === 0;
}

function PullRequestRow({
  pr,
  position,
  ticked,
  onTick,
}: {
  pr: EpicPullRequest;
  position: number;
  ticked: boolean;
  onTick: (value: boolean) => void;
}) {
  const markReady = useMarkPullRequestReady();
  const { toast } = useToast();
  const merged = pr.state === "merged";
  const page = pr.id ? `/pull-requests/${pr.id}` : null;
  const title = pr.title ?? `Pull request #${String(pr.number)}`;

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2" data-testid={`epic-pr-${String(pr.number)}`}>
      <Checkbox
        checked={merged || ticked}
        disabled={merged || pr.state === "closed"}
        onCheckedChange={onTick}
        aria-label={`Merged #${String(pr.number)}`}
        title={merged ? "Merged on GitHub" : "Tick once you've merged it"}
      />
      <span className="text-sm text-muted-foreground w-5">{position}.</span>
      <div className="min-w-0 flex-1">
        {page ? (
          <Link to={page} className="text-sm font-medium hover:underline">
            #{pr.number} {title}
          </Link>
        ) : (
          <a href={pr.htmlUrl} target="_blank" rel="noreferrer" className="text-sm font-medium hover:underline">
            #{pr.number} {title}
          </a>
        )}
        <a
          href={pr.htmlUrl}
          target="_blank"
          rel="noreferrer"
          className="ml-2 inline-flex items-center text-xs text-muted-foreground hover:underline"
        >
          GitHub <ExternalLink className="h-3 w-3 ml-0.5" />
        </a>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {merged && (
          <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">Merged</Badge>
        )}
        {pr.state === "closed" && <Badge variant="secondary">Closed without merging</Badge>}
        {pr.draft && (
          <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">Draft</Badge>
        )}
        <ChecksBadge pr={pr} />
        {pr.draft && pr.id && (
          <Button
            size="sm"
            variant="outline"
            disabled={markReady.isPending}
            onClick={() => {
              markReady.mutate(pr.id ?? "", {
                onError: (error) => {
                  toast(error instanceof ApiError ? error.message : "Couldn't mark it ready");
                },
              });
            }}
          >
            Mark ready
          </Button>
        )}
        {pr.state === "open" && !pr.draft && ciGreen(pr) &&
          (page ? (
            <Button size="sm" asChild>
              <Link to={page}>Review and merge</Link>
            </Button>
          ) : (
            <Button size="sm" asChild>
              <a href={pr.htmlUrl} target="_blank" rel="noreferrer">
                Squash and merge on GitHub
              </a>
            </Button>
          ))}
      </div>
    </li>
  );
}

/**
 * ER-130: the epic's pull requests, in the order to merge them, with what the
 * owner needs to act on each (link, CI, draft → Mark ready, merge). On a build
 * page (`job`), it also offers "All merged — continue" / "Restart" and lets
 * the owner skip unfinished features on every later run.
 */
export function WaitingPullRequests({ epicId, job }: { epicId: string; job?: Job }) {
  const prs = useEpicPullRequests(epicId, true, job !== undefined && !TERMINAL.has(job.status));
  const { toast } = useToast();
  const navigate = useNavigate();
  const continueRun = useContinueRun("continue");
  const restartRun = useContinueRun("restart");
  const [ticked, setTicked] = useState<Record<number, boolean>>({});
  const choices = job ? continueChoices(job) : { canContinue: false, canRestart: false };
  // The epic's features, to offer skipping the unfinished ones (one page is plenty).
  const features = useQuery({
    // Its own key: useFeatures caches this list as pages (an infinite query)
    // under ["features", "list", filters], and sharing it crashed the idea page.
    queryKey: ["features", "list", { epicId, limit: 100 }, "plain"],
    queryFn: () => featuresApi.list({ epicId, limit: 100 }),
    enabled: choices.canContinue,
  });
  const initialDeferred = useMemo(() => (job ? payloadFacts(job.payload).deferredIds : []), [job]);
  const [deferred, setDeferred] = useState<string[] | null>(null);
  const deferredIds = deferred ?? initialDeferred;

  const list = prs.data ?? [];
  const open = list.filter((pr) => pr.state === "open");
  const unfinished = (features.data?.data ?? []).filter(
    (f) => f.status?.category !== "completed" && f.status?.category !== "canceled"
  );

  if (list.length === 0 && !choices.canContinue) return null;

  const busy = continueRun.isPending || restartRun.isPending;
  const run = (mode: "continue" | "restart") => {
    if (!job) return;
    const mutation = mode === "continue" ? continueRun : restartRun;
    mutation.mutate(
      { jobId: job.id, ...(deferred !== null && { deferredFeatureIds: deferred }) },
      {
        onSuccess: (result) => {
          toast(
            result.waitingForDeploy
              ? "Merges confirmed. The build continues once the deploy finishes."
              : result.mode === "resumed"
                ? "The build is continuing."
                : "The build is starting again."
          );
          if (result.mode === "restarted") void navigate(`/mailbox/jobs/${result.jobId}`);
        },
        onError: (error) => {
          toast(error instanceof ApiError ? error.message : "Couldn't continue the build");
          void prs.refetch();
        },
      }
    );
  };

  return (
    <section className="border-t px-4 md:px-6 py-4 space-y-3" data-testid="waiting-pull-requests">
      <div className="flex items-center gap-2">
        <GitPullRequest className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold">
          {open.length > 0
            ? `${String(open.length)} pull request${open.length === 1 ? " needs" : "s need"} your review and merge, in this order`
            : list.length > 0
              ? "Pull requests for this epic"
              : "No pull requests waiting"}
        </h2>
      </div>
      {prs.isLoading && <p className="text-sm text-muted-foreground">Loading pull requests…</p>}
      {list.length > 0 && (
        <ol className="divide-y">
          {list.map((pr, i) => (
            <PullRequestRow
              key={pr.htmlUrl}
              pr={pr}
              position={i + 1}
              ticked={ticked[pr.number] === true}
              onTick={(value) => {
                setTicked((prev) => ({ ...prev, [pr.number]: value }));
                if (value) void prs.refetch();
              }}
            />
          ))}
        </ol>
      )}

      {choices.canContinue && unfinished.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Skip any features for now? Every later run skips them too.
          </p>
          <ul className="space-y-1">
            {unfinished.map((f) => {
              const skipped = deferredIds.includes(f.id);
              return (
                <li key={f.id} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={skipped}
                    aria-label={`Skip ${f.identifier}`}
                    onCheckedChange={(value) => {
                      setDeferred(value ? [...deferredIds, f.id] : deferredIds.filter((id) => id !== f.id));
                    }}
                  />
                  <span className={cn(skipped && "line-through text-muted-foreground")}>
                    {f.identifier} {f.title}
                  </span>
                  {skipped && <span className="text-xs text-muted-foreground">Deferred — will be skipped</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {(choices.canContinue || choices.canRestart) && (
        <div className="flex flex-wrap gap-2">
          {choices.canContinue && (
            <Button
              size="sm"
              disabled={busy}
              onClick={() => {
                run("continue");
              }}
            >
              {continueRun.isPending && <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />}
              All merged — continue
            </Button>
          )}
          {choices.canRestart && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => {
                run("restart");
              }}
            >
              <RotateCcw className="h-3 w-3 mr-1.5" />
              Restart
            </Button>
          )}
        </div>
      )}
    </section>
  );
}
